/**
 * Ponte fra la libreria anatomica open `body-muscles` (89 muscoli fini, MIT,
 * zero-dep) e le 22 zone canoniche del backend LeoPart
 * (`BODY_MAP_ZONE` / `INJURY_BODY_ZONE`, cfr. `enums/bodyZoneLabels.ts`).
 *
 * Le 22 zone sono più grosse dei singoli muscoli: qui ogni muscolo viene
 * "piegato" sulla zona corrispondente, così ogni regione cliccabile della
 * figura anatomica corrisponde 1:1 a una zona reale dei dati (fastidi/infortuni).
 */
import { FRONT_MUSCLES, BACK_MUSCLES } from 'body-muscles';
import type { MuscleDef } from 'body-muscles';

/** Zone non lateralizzate nel backend: mai suffisso `_dx`/`_sx`. */
const NON_LATERAL = new Set(['head_neck', 'chest_ribs', 'upper_back', 'lumbar']);

/**
 * id muscolo body-muscles → zona base LeoPart (senza lato).
 * L'ordine dei match conta: `lats-upper`/`lats-mid` → schiena alta, mentre
 * `lats-lower` → lombare, quindi il primo blocco esclude di proposito
 * `lats-lower`.
 */
function baseZone(id: string): string | null {
  if (/head|face|neck|nape/.test(id)) return 'head_neck';
  if (/deltoid|shoulder|biceps|triceps/.test(id)) return 'shoulder'; // spalla + braccio alto
  if (/elbow/.test(id)) return 'elbow';
  if (/forearm|hand/.test(id)) return 'wrist_hand'; // avambraccio + polso/mano
  if (/chest|abs|serratus|obliques/.test(id)) return 'chest_ribs'; // tronco anteriore
  if (/traps|lats-upper|lats-mid/.test(id)) return 'upper_back';
  if (/lats-lower|spine|lower-back/.test(id)) return 'lumbar';
  if (/hip-flexor|adductors|gluteus/.test(id)) return 'hip_groin'; // anca/inguine + glutei
  if (/quads/.test(id)) return 'quadricep';
  if (/hamstrings/.test(id)) return 'hamstring';
  if (/tibialis|calves/.test(id)) return 'calf'; // gamba bassa
  if (/knee/.test(id)) return 'knee';
  if (/foot/.test(id)) return 'ankle_foot';
  return null;
}

/**
 * id muscolo body-muscles → chiave zona canonica LeoPart, con lato `_dx`/`_sx`
 * dedotto dal suffisso `-right`/`-left` (tranne le zone non lateralizzate).
 * Ritorna `null` per i muscoli che non mappano su nessuna zona del backend.
 */
export function muscleIdToZone(id: string): string | null {
  const base = baseZone(id);
  if (!base) return null;
  if (NON_LATERAL.has(base)) return base;
  const side = /-left/.test(id) ? 'sx' : /-right/.test(id) ? 'dx' : null;
  return side ? `${base}_${side}` : base;
}

/** Muscolo pronto per il rendering: path SVG + zona canonica pre-calcolata. */
export interface AnatomyMuscle {
  id: string;
  name: string;
  path: string;
  /** Zona canonica LeoPart, o `null` se il muscolo non mappa su alcuna zona. */
  zone: string | null;
}

function withZone(list: readonly MuscleDef[]): AnatomyMuscle[] {
  return list.map((m) => ({
    id: m.id,
    name: m.name,
    path: m.path,
    zone: muscleIdToZone(m.id),
  }));
}

/** Muscoli della vista frontale, arricchiti con la zona canonica. */
export const FRONT_ANATOMY: AnatomyMuscle[] = withZone(FRONT_MUSCLES);

/** Muscoli della vista posteriore, arricchiti con la zona canonica. */
export const BACK_ANATOMY: AnatomyMuscle[] = withZone(BACK_MUSCLES);

/**
 * viewBox per vista. Front e back di `body-muscles` vivono nello stesso spazio
 * coordinate condiviso (front x 0-35, back x 37-72): ogni vista va ritagliata
 * sulla propria metà, altrimenti il back non entra nell'inquadratura.
 */
export const ANATOMY_VIEWBOX: Record<'front' | 'back', string> = {
  front: '0 0 35 93',
  back: '37 0 35 93',
};
